import { AppPieChart } from '@/components/charts/pieChart/AppPieChart';
import { AppCard } from '@/components/ui/AppCard';
import { axiosGetRequest } from '@/services/Request';
import { endpoints } from '@/services/endpoints';
import { useQuery } from '@tanstack/react-query';
import { Users, BookOpen, Ticket, TrendingUp, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router';
import LogsComponent from './LogsConponent';
import BooksPerCategoryChart from '../Charts/Books/BooksPerCategoryChart';
import { Skeleton } from '@/components/ui/Skeleton';

// ─── Stat Card ─────────────────────────────────────────────────────────────
function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
  href,
  isLoading,
}: {
  title: string;
  value?: number | string;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
  href: string;
  isLoading?: boolean;
}) {
  return (
    <Link to={href} className="block group">
      <div className="rounded-xl border border-coal-200 bg-white p-5 shadow-card transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
        <div className="flex items-start justify-between">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <ArrowUpRight className="h-4 w-4 text-coal-300 transition-colors group-hover:text-primary" />
        </div>
        <div className="mt-4">
          {isLoading ? (
            <>
              <Skeleton className="h-8 w-20 rounded mb-1" />
              <Skeleton className="h-4 w-28 rounded" />
            </>
          ) : (
            <>
              <p className="text-2xl font-bold text-coal-900">{value ?? '—'}</p>
              <p className="mt-0.5 text-sm text-coal-500">{title}</p>
              {subtitle && (
                <p className="mt-1 text-xs text-coal-400">{subtitle}</p>
              )}
            </>
          )}
        </div>
      </div>
    </Link>
  );
}

// ─── Most Active Users ──────────────────────────────────────────────────────
function MostActiveUsers() {
  const { data, isLoading } = useQuery({
    queryKey: [endpoints.getMostActiveUsers],
    queryFn: () => axiosGetRequest(endpoints.getMostActiveUsers),
    select: d => d?.result || d?.data || [],
  });

  return (
    <AppCard>
      <div className="mb-4 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-primary" />
        <h3 className="text-base font-semibold text-coal-900">Most Active Users</h3>
      </div>
      {isLoading ? (
        <div className="space-y-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-3.5 w-32 rounded" />
                <Skeleton className="h-3 w-20 rounded" />
              </div>
              <Skeleton className="h-5 w-10 rounded-full" />
            </div>
          ))}
        </div>
      ) : data?.length > 0 ? (
        <div className="space-y-2">
          {data.slice(0, 7).map((user: any, i: number) => (
            <Link
              key={user._id || i}
              to={`/users/profile/?uid=${user._id}`}
              className="flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-coal-50"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary/80 to-primary text-xs font-bold text-white flex-shrink-0">
                {user.fullName?.[0]?.toUpperCase() || '#'}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-coal-800">{user.fullName}</p>
                <p className="text-xs text-coal-400 capitalize">{user.role}</p>
              </div>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                {user.activityCount ?? user.count ?? i + 1}
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <p className="py-4 text-center text-sm text-coal-400">No data available</p>
      )}
    </AppCard>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: [endpoints.getAllUsers, 'stats'],
    queryFn: () => axiosGetRequest(endpoints.getAllUsers, { limit: 1 }),
  });

  const { data: booksData, isLoading: booksLoading } = useQuery({
    queryKey: [endpoints.getAllBooks, 'stats'],
    queryFn: () => axiosGetRequest(endpoints.getAllBooks, { limit: 1 }),
  });

  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: [endpoints.getAllTickets, 'stats'],
    queryFn: () => axiosGetRequest(endpoints.getAllTickets, { limit: 1 }),
  });

  const { data: pendingBooksData, isLoading: pendingLoading } = useQuery({
    queryKey: [endpoints.getAllBooks, 'pending'],
    queryFn: () => axiosGetRequest(endpoints.getAllBooks, { limit: 1, bookStatus: 'pending' }),
  });

  const { data: genderData, isLoading: genderLoading } = useQuery({
    queryKey: [endpoints.getMalesAndFemalesUsersCount],
    queryFn: () => axiosGetRequest(endpoints.getMalesAndFemalesUsersCount),
    select: data => data.data,
  });

  const genderConfig = {
    male: { label: 'Male', color: '#3B82F6' },
    female: { color: '#A855F7', label: 'Female' },
  };

  const totalUsers = usersData?.pagination?.totalItems;
  const totalBooks = booksData?.pagination?.totalItems;
  const totalTickets = ticketsData?.pagination?.totalItems;
  const pendingBooks = pendingBooksData?.pagination?.totalItems;

  return (
    <main className="space-y-5">
      {/* ── KPI Stats Row ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={totalUsers?.toLocaleString()}
          icon={Users}
          color="bg-blue-500"
          href="/users"
          isLoading={usersLoading}
        />
        <StatCard
          title="Total Books"
          value={totalBooks?.toLocaleString()}
          subtitle={pendingBooks ? `${pendingBooks} pending review` : undefined}
          icon={BookOpen}
          color="bg-emerald-500"
          href="/books"
          isLoading={booksLoading || pendingLoading}
        />
        <StatCard
          title="Total Tickets"
          value={totalTickets?.toLocaleString()}
          icon={Ticket}
          color="bg-orange-500"
          href="/tickets"
          isLoading={ticketsLoading}
        />
        <StatCard
          title="Pending Review"
          value={pendingBooks?.toLocaleString()}
          subtitle="Books awaiting approval"
          icon={TrendingUp}
          color="bg-purple-500"
          href="/books"
          isLoading={pendingLoading}
        />
      </div>

      {/* ── Charts Row ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-6">
        <div className="lg:col-span-4">
          <AppCard>
            <h3 className="mb-4 text-base font-semibold text-coal-900">Users by Gender</h3>
            <AppPieChart
              isLoading={genderLoading}
              chartData={genderData || []}
              dataKey="count"
              nameKey="gender"
              config={genderConfig}
              innerData={{
                value: genderData?.reduce?.((sum: number, d: any) => sum + (d.count || 0), 0) || 'N/A',
                title: 'Total Users',
              }}
            />
          </AppCard>
        </div>
        <LogsComponent className="lg:col-span-2 h-[calc(100vh-10rem)]" />
      </div>

      {/* ── Books + Active Users ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <BooksPerCategoryChart />
        <MostActiveUsers />
      </div>
    </main>
  );
}
